import { OnGridClickEventType } from "@collins/react-componments/dist/types/eventHandlers";
import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import { DiaryContextType } from "../../../../../context/DiaryContext";
import {
  BookableArea,
  BookingOverride,
  BookingRule,
  BookingRules,
  BookingType,
  NumPeopleOverride
} from "../../../../../types/venues";

const getTimeToNextQuarterHour = () => {
  const nextQuarterHour = new Date();
  nextQuarterHour.setMinutes(
    Math.ceil(nextQuarterHour.getMinutes() / 15) * 15,
    0,
    0
  );

  return nextQuarterHour.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

export type ButtonType = {
  visible: boolean;
  disabled?: boolean;
  text: string;
  onClick: () => void | Promise<void>;
};
export type AddBookingState = {
  step: number;
  maxSteps: number;
  booking: ViewBooking;
  waitlistSlotMins?: number;
};
export const defaultState: AddBookingState = {
  step: 1,
  maxSteps: 1,
  booking: {
    date: new Date(),
    time: getTimeToNextQuarterHour(),
    numPeople: 2,
    walkIn: true,
    status: ViewBookingStatusEnum.Complete,
    firstName: "",
    lastName: "",
    currentStage: "",
    waitlisted: false,
    assignedAreas: [],
    partnerSource: ""
  }
};

export const getDefaultDuration = (
  bookingContext: DiaryContextType,
  booking: ViewBooking
): number | null => {
  // Get the booking type details
  const bookingTypes = bookingContext.venueConfig?.booking_types;

  const typeDetails = bookingTypes?.find(
    (type) => type.id === booking.type?.id
  );

  if (!typeDetails && booking.date) {
    // If we're adding for today, try to set a default walk-in duration
    if (
      dateIsToday(booking.date, bookingContext?.gridConfig?.startMinutes) &&
      booking.numPeople
    ) {
      const defaultDuration = getDefaultDurationByNumPeople(
        bookingContext,
        booking
      );

      if (defaultDuration && defaultDuration !== booking.duration) {
        return defaultDuration;
      }
    }
    return null;
  }

  const bookingRules = typeDetails
    ? getRulesForBooking(typeDetails, booking)
    : undefined;

  let duration = booking.duration;
  // Try to set a default duration
  if (typeDetails?.no_duration) {
    return null;
  } else if (bookingRules?.max_duration) {
    duration = bookingRules?.max_duration;
  } else if (bookingRules?.min_duration) {
    duration = bookingRules?.min_duration;
  }

  return duration ?? null;
};

const dateIsToday = (date: Date, startMinutes: number) => {
  const now = new Date();
  const dayStart = new Date(date);
  dayStart.setHours(0, startMinutes);
  const nextDayStart = new Date(date);
  nextDayStart.setHours(24, startMinutes);

  return now >= dayStart && now < nextDayStart;
};

const getDefaultDurationByNumPeople = (
  bookingContext: DiaryContextType,
  booking: ViewBooking
) => {
  if (!booking.numPeople) {
    return null;
  }

  const defaultDurations =
    bookingContext?.venueConfig?.walk_in_rules?.default_duration;

  let defaultDuration = defaultDurations?.[`${booking.numPeople}`];
  if (!defaultDuration && booking.numPeople && booking.numPeople > 0) {
    let maxDefaultDurationNumPeople = 0;
    for (const key in defaultDurations) {
      const defaultDurationNumPeople = parseInt(key);
      if (
        defaultDurationNumPeople > maxDefaultDurationNumPeople &&
        !!defaultDurations[key]
      ) {
        maxDefaultDurationNumPeople = defaultDurationNumPeople;
      }
    }
    defaultDuration = defaultDurations?.[maxDefaultDurationNumPeople];
  }

  return defaultDuration;
};

const getRulesForBooking = (
  bookingTypeDetails: BookingType,
  booking: ViewBooking
) => {
  // Get rules for the given date
  const foundRules = getRulesForDate(
    bookingTypeDetails,
    booking.date ?? new Date()
  );

  // Get override rules for the number of guests
  const numPeopleOverrides = getNumPeopleOverrides(
    bookingTypeDetails,
    booking.numPeople ?? 0
  );

  return Object.assign(foundRules ?? {}, numPeopleOverrides);
};

export const getRulesForDate = (
  bookingTypeDetails: BookingType,
  date: Date
): BookingOverride | BookingRule | null => {
  const weekday = date
    .toLocaleDateString(window.locale, { weekday: "long" })
    .toLowerCase() as keyof BookingRules;
  let foundRules = false;
  let bookingOverrideRules: BookingOverride | null = null;

  if (!bookingTypeDetails?.booking_rules) {
    return null;
  }

  // Try to find an override that matches the given date
  if (bookingTypeDetails.booking_overrides) {
    bookingTypeDetails.booking_overrides.forEach((rules) => {
      if (foundRules) {
        return;
      }

      const dateFrom = new Date(rules.date_from);
      const dateTo = new Date(rules.date_to);

      if (date >= dateFrom && date <= dateTo) {
        foundRules = true;
        bookingOverrideRules = rules;
      }
    });
  }

  // Get the rules for this weekday if we didn't find an override
  if (!bookingOverrideRules) {
    return bookingTypeDetails.booking_rules?.[weekday] ?? null;
  }

  return bookingOverrideRules as BookingOverride;
};

const getNumPeopleOverrides = (
  bookingTypeDetails: BookingType,
  numPeople: number
): NumPeopleOverride | null => {
  let numPeopleOverrides: NumPeopleOverride | null = null;
  let foundOverride = false;

  if (!bookingTypeDetails.num_people_overrides) {
    return numPeopleOverrides;
  }

  // Check all overrides and return the first match
  bookingTypeDetails.num_people_overrides.forEach((override) => {
    if (
      !foundOverride &&
      numPeople >= override.num_people_from &&
      numPeople <= override.num_people_to
    ) {
      numPeopleOverrides = override;
      foundOverride = true;
    }
  });

  return numPeopleOverrides;
};

export const getDurationData = (): Array<{
  label: string;
  value: any;
}> => {
  const intervalMins = 15;
  const maxDurationMins = 12 * 60; // 12 hours max

  const data = [
    {
      label: "Until close",
      value: -1
    }
  ];

  for (
    let durationMins = intervalMins;
    durationMins < maxDurationMins;
    durationMins += intervalMins
  ) {
    const durationHours = durationMins / 60;
    const hourPart = Math.floor(durationHours);
    const minutePart = durationMins % 60;
    const hourLabel = hourPart === 1 ? "1 hr" : `${hourPart} hrs`;
    const minuteLabel = `${minutePart} min`;

    data.push({
      label: `${hourLabel} ${minuteLabel}`,
      value: durationMins
    });
  }
  return data;
};

type SetSelectionFromGridProps = {
  gridAreaClick: OnGridClickEventType;
  bookingContext: DiaryContextType;
  booking: ViewBooking;
  updateBookingAndDuration: (booking: Partial<ViewBooking>) => void;
  setSelectedAreas: (area: BookableArea[]) => void;
};
export const setSelectionFromGrid = ({
  gridAreaClick,
  bookingContext,
  booking,
  updateBookingAndDuration,
  setSelectedAreas
}: SetSelectionFromGridProps) => {
  // set area, guests and time
  const newBooking: Partial<ViewBooking> = {
    numPeople:
      gridAreaClick.newValue.area.max_people_seated ?? booking.numPeople
  };

  if (gridAreaClick.newValue.cell !== -1) {
    const minutesTotal =
      bookingContext.gridConfig.startMinutes +
      gridAreaClick.newValue.cell * bookingContext.gridConfig.colMinutes;

    const dateTime = new Date();
    dateTime.setHours(Math.floor(minutesTotal / 60), minutesTotal % 60, 0, 0);

    const time = dateTime.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
    newBooking.time = time;
  } else {
    // cell === -1 means the trigger has no time dimension (e.g. floorplan
    // double-tap). Prefill with the next quarter hour from now so the form
    // doesn't render with an empty/00:00 start time.
    newBooking.time = getTimeToNextQuarterHour();
  }

  updateBookingAndDuration(newBooking);

  const gridAreaId =
    gridAreaClick.newValue.area.id === "unassigned"
      ? ""
      : (gridAreaClick.newValue.area.id ?? "");

  const zones = [...(bookingContext.venueConfig?.bookable_area_zones ?? [])];

  const zone =
    zones.find((z) => z.id === gridAreaClick.newValue.area.zone)?.name ??
    "No Zone";

  let area = [{ ...gridAreaClick.newValue.area, id: gridAreaId, zone }];
  if (!gridAreaId) {
    area = [];
  }

  setSelectedAreas(area);
};

type ToggleAreaFromGridProps = {
  gridAreaClick: OnGridClickEventType;
  bookingContext: DiaryContextType;
  selectedAreas: BookableArea[];
  setSelectedAreas: (area: BookableArea[]) => void;
};
export const toggleAreaFromGrid = ({
  gridAreaClick,
  bookingContext,
  selectedAreas,
  setSelectedAreas
}: ToggleAreaFromGridProps) => {
  const gridAreaId =
    gridAreaClick.newValue.area.id === "unassigned"
      ? ""
      : (gridAreaClick.newValue.area.id ?? "");

  if (!gridAreaId) {
    setSelectedAreas([]);
    return;
  }

  const alreadySelectedArea = selectedAreas.find(
    (area) => area.id === gridAreaId
  );

  const zones = [...(bookingContext.venueConfig?.bookable_area_zones ?? [])];
  const zone =
    zones.find((z) => z.id === gridAreaClick.newValue.area.zone)?.name ??
    "No Zone";

  let setAreas = selectedAreas;
  if (alreadySelectedArea) {
    setAreas = selectedAreas.filter((area) => area.id !== gridAreaId);
  } else {
    setAreas.push({
      ...gridAreaClick.newValue.area,
      id: gridAreaId,
      zone
    });
  }

  setSelectedAreas([...setAreas]);
};

export const toggleHighlightArea = (areaId: string) => {
  toggleHighlightFloorplanArea(areaId);
  toggleHighlightDiaryArea(areaId);
};

export const clearAllHighlightedAreas = () => {
  clearAllHighlightedFloorplanAreas();
  clearAllHighlightedDiaryAreas();
};

const toggleHighlightDiaryArea = (areaId: string) => {
  const element = document.querySelector(`[data-area-id="${areaId}"]`);
  if (element?.classList.contains("HighlightArea")) {
    element.classList.remove("HighlightArea");
  } else if (element) {
    element.classList.add("HighlightArea");
  }
};

const clearAllHighlightedDiaryAreas = () => {
  const elements = document.querySelectorAll("[data-area-id]");
  elements.forEach((element) => {
    element.classList.remove("HighlightArea");
  });
};

const toggleHighlightFloorplanArea = (areaId: string) => {
  const element = document.querySelector(
    `[data-floorplan-area-id="${areaId}"]`
  );
  if (element?.classList.contains("AreaIsSelected")) {
    element.classList.remove("AreaIsSelected");
  } else if (element) {
    element.classList.add("AreaIsSelected");
  }
};

const clearAllHighlightedFloorplanAreas = () => {
  const elements = document.querySelectorAll("[data-floorplan-area-id]");
  elements.forEach((element) => {
    element.classList.remove("AreaIsSelected");
  });
};
